import { useMemo, useState } from "react";

type RelatedContract = {
  id: string;
  contractNumber: string;
  sector: string;
  address: string;
};

type Props = {
  currentRut: string;
  customerName: string;
  contracts: RelatedContract[];
  saving?: boolean;
  onClose: () => void;
  onSave: (nextRut: string) => Promise<void>;
};

function normalizeRutInput(value: string): string {
  const cleaned = value.replace(/[^0-9kK]/g, "").toUpperCase();
  if (cleaned.length <= 1) return cleaned;
  const body = cleaned.slice(0, -1);
  const dv = cleaned.slice(-1);
  return `${body}-${dv}`;
}

export default function ActualizarRutModal({
  currentRut,
  customerName,
  contracts,
  saving = false,
  onClose,
  onSave,
}: Props) {
  const [nextRut, setNextRut] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [confirmedRut, setConfirmedRut] = useState("");

  const normalizedCurrentRut = useMemo(() => currentRut.trim(), [currentRut]);

  function validateRutCandidate(): string | null {
    const normalizedNextRut = normalizeRutInput(nextRut);

    if (!normalizedNextRut.includes("-")) {
      setError("Ingresa el nuevo RUT con dígito verificador.");
      return null;
    }

    if (normalizedNextRut === normalizedCurrentRut) {
      setError("El nuevo RUT debe ser distinto al actual.");
      return null;
    }

    setError(null);
    return normalizedNextRut;
  }

  function handleContinue(event: React.FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const candidate = validateRutCandidate();
    if (!candidate) return;
    setConfirmedRut(candidate);
    setIsConfirming(true);
  }

  async function handleConfirmSave(): Promise<void> {
    const candidate = confirmedRut || validateRutCandidate();
    if (!candidate) return;
    await onSave(candidate);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={saving ? undefined : onClose} />
      <div className="relative w-full max-w-4xl overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="flex items-center justify-between bg-teal-600 px-8 py-5 text-white">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/15">
              <i className="ri-id-card-line text-base" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Actualizar RUT</h2>
              <p className="text-sm text-teal-100">{customerName}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/20 transition-colors hover:bg-white/30 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <form onSubmit={handleContinue} className="space-y-5 p-6">
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-2xl border border-amber-200 bg-amber-50/30 p-5 shadow-sm">
              <h3 className="mb-4 text-lg font-semibold text-amber-800">RUT actual</h3>
              <div className="space-y-3">
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">RUT</label>
                  <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 text-base font-semibold text-gray-800">
                    {normalizedCurrentRut || "-"}
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Cliente</label>
                  <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 text-base text-gray-800">
                    {customerName}
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-gray-50/40 p-5 shadow-sm">
              <h3 className="mb-4 text-lg font-semibold text-gray-800">RUT nuevo</h3>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Nuevo RUT <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={nextRut}
                  onChange={(event) => {
                    setNextRut(normalizeRutInput(event.target.value));
                    if (error) setError(null);
                    if (isConfirming) {
                      setIsConfirming(false);
                      setConfirmedRut("");
                    }
                  }}
                  placeholder="12345678-9"
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-base outline-none transition-colors focus:border-teal-400"
                />
              </div>
              <div className="mt-5 flex justify-end">
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-xl bg-slate-600 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Continuar
                </button>
              </div>
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
              <div className="border-b border-gray-100 px-4 py-3 text-base font-semibold text-gray-800">
                Contratos asociados al cliente
              </div>
              <div className="divide-y divide-gray-100">
                {contracts.length === 0 ? (
                  <div className="px-4 py-4 text-sm text-gray-500">Sin contratos asociados.</div>
                ) : (
                  contracts.map((contract) => (
                    <div key={contract.id} className="grid grid-cols-3 gap-3 px-4 py-4 text-sm">
                      <div className="font-semibold text-gray-800">{contract.contractNumber}</div>
                      <div className="text-gray-600">{contract.sector || "-"}</div>
                      <div className="text-gray-600">{contract.address || "-"}</div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50/30 p-5 shadow-sm">
              <h3 className="mb-3 text-base font-semibold text-gray-800">Confirmación</h3>
              {isConfirming ? (
                <div className="space-y-4">
                  <div className="rounded-xl border border-teal-200 bg-teal-50 px-4 py-3 text-sm text-teal-800">
                    Se actualizará el RUT de <span className="font-semibold">{normalizedCurrentRut}</span> a{" "}
                    <span className="font-semibold">{confirmedRut}</span>.
                  </div>
                  <div className="space-y-2 text-sm leading-6 text-gray-600">
                    <p>Se modificará solo el RUT del cliente seleccionado.</p>
                    <p>Los contratos, lecturas, pagos y operaciones seguirán asociados al mismo cliente.</p>
                    <p>Las futuras boletas y documentos usarán el nuevo RUT.</p>
                  </div>
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => void handleConfirmSave()}
                      disabled={saving}
                      className="rounded-xl bg-teal-600 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {saving ? "Grabando..." : "Grabar"}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2 text-sm leading-6 text-gray-600">
                  <p>Se modificará solo el RUT del cliente seleccionado.</p>
                  <p>Los contratos, lecturas, pagos y operaciones seguirán asociados al mismo cliente.</p>
                  <p>Las futuras boletas y documentos usarán el nuevo RUT.</p>
                </div>
              )}
            </div>
          </div>

          {error ? <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div> : null}

          <div className="flex justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded-xl bg-gray-100 px-5 py-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
